import type { Anchor, Rect, Vec2 } from './types';

// Screen dimensions (logical size, not affected by retina scaling)
export const Screen = {
    width: 1280,
    height: 720,
    center: (): Vec2 => [640, 360],
    centerX: (): number => 640,
    centerY: (): number => 360,
    topLeft: (): Vec2 => [0, 0],
    topRight: (): Vec2 => [1280, 0],
    bottomLeft: (): Vec2 => [0, 720],
    bottomRight: (): Vec2 => [1280, 720],
};

export const vec = {
    add: ([x1, y1]: Vec2, [x2, y2]: Vec2): Vec2 => [x1 + x2, y1 + y2],
    sub: ([x1, y1]: Vec2, [x2, y2]: Vec2): Vec2 => [x1 - x2, y1 - y2],
    lerp: ([x1, y1]: Vec2, [x2, y2]: Vec2, u: number): Vec2 =>
        [x1 + (x2 - x1) * u, y1 + (y2 - y1) * u],
};

export const Vec = (x: number, y: number): Vec2 => [x, y];

export const lerp = (a: number, b: number, u: number): number => a + (b - a) * u;

export const angle = (deg: number): number => {
    return deg * (Math.PI / 180);
};

/**
 * Compute the local position of an anchor point from an axis-aligned bounding box.
 *
 * Use this to implement `SceneGeometry.resolveAnchor` for external document models:
 *
 * ```ts
 * resolveAnchor(obj, anchor) {
 *   const bounds = myDoc.getBounds(obj);
 *   return resolveAnchorFromBounds(bounds, anchor);
 * }
 * ```
 *
 * Note: the `baseline` and `baselineLeft` anchors fall back to the bottom edge.
 * If your objects have text baselines, handle those two cases before delegating here.
 */
export function resolveAnchorFromBounds(bounds: Rect, anchor: Anchor): Vec2 {
    const { x, y, w, h } = bounds;

    switch (anchor) {
        case 'origin':     return [0, 0];
        case 'topLeft':    return [x,         y        ];
        case 'top':        return [x + w / 2, y        ];
        case 'topRight':   return [x + w,     y        ];
        case 'left':       return [x,         y + h / 2];
        case 'center':     return [x + w / 2, y + h / 2];
        case 'right':      return [x + w,     y + h / 2];
        case 'bottomLeft': return [x,         y + h    ];
        case 'bottom':     return [x + w / 2, y + h    ];
        case 'bottomRight':return [x + w,     y + h    ];
        // baseline falls back to bottom edge — override for text objects
        case 'baselineLeft': return [x,         y + h];
        case 'baseline':     return [x + w / 2, y + h];
    }
}


export function applySR(
    [x, y]: Vec2,
    scale: number,
    rotate: number, // radians
    scaleX: number = 1,
    scaleY: number = 1
): Vec2 {
    // scale (uniform * non-uniform)
    const sx = x * scale * scaleX;
    const sy = y * scale * scaleY;

    // rotate
    const c = Math.cos(rotate);
    const s = Math.sin(rotate);

    return [
        sx * c - sy * s,
        sx * s + sy * c,
    ];
}
